import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const CATEGORIES = [
  {
    id: 'ocean',
    image: 'https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/0x3s3he2_Barefoot0103.jpg',
  },
  {
    id: 'earth',
    image: '/mountain.webp',
  },
  {
    id: 'wellness',
    image: 'https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/g7nb8bjd_Barefoot3016.jpg',
  },
  {
    id: 'morabeza',
    image: 'https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/ywlmj00z_Barefoot0694.jpg',
  },
];

const ExplorationCard = ({ category, compact }) => {
  const { t } = useLanguage();
  const title = t(`explorations.${category.id}.title`);
  const activities = t(`explorations.${category.id}.activities`);

  return (
    <div
      className={compact 
        ? 'flex-shrink-0 w-[280px] snap-center bg-white' 
        : 'flex flex-col bg-white'}
      data-testid={compact ? `exploration-card-mobile-${category.id}` : `exploration-card-${category.id}`}
    >
      {/* Image */}
      <div className={`${compact ? 'mb-3' : 'mb-6'} aspect-[4/3] overflow-hidden bg-white`}>
        <img
          src={category.image}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <h3 className={`font-serif text-black ${compact ? 'text-lg mb-2' : 'text-2xl lg:text-3xl mb-3'}`}>
        {title}
      </h3>
      {!compact && (
        <p className="text-black/70 text-sm font-light leading-relaxed mb-4 text-justify">
          {t(`explorations.${category.id}.description`)}
        </p>
      )}

      {/* Activities list */}
      <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-2'}`}>
        {activities?.map((activity) => (
          <span 
            key={activity}
            className={compact 
              ? 'text-[10px] text-black/60 border border-black/20 px-2 py-0.5'
              : 'text-xs text-black/60 border border-black/20 px-3 py-1'}
          >
            {activity}
          </span>
        ))}
      </div>
    </div>
  );
};

const Explorations = () => {
  const { t } = useLanguage();
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="explorations" 
      className="py-10 md:py-12 bg-white" 
      data-testid="explorations-section"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 px-6 md:px-12">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-black mb-6">
            {t('explorations.title')}
          </h2>
          <p className="text-lg md:text-xl text-black/70 max-w-3xl mx-auto font-light text-center">
            {t('explorations.subtitle')}
          </p>
        </div>

        {/* Mobile: Horizontal Scroll */}
        <div className="relative lg:hidden bg-white">
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide px-12 pb-4 snap-x snap-mandatory bg-white"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {CATEGORIES.map((category) => (
              <ExplorationCard key={category.id} category={category} compact />
            ))}
          </div>

          <button
            onClick={() => scroll('left')}
            className="absolute left-1 top-[100px] z-10 w-9 h-9 bg-white/90 hover:bg-white flex items-center justify-center shadow-lg rounded-full"
            data-testid="explorations-scroll-left"
          >
            <ChevronLeft size={18} className="text-black" />
          </button>

          <button
            onClick={() => scroll('right')}
            className="absolute right-1 top-[100px] z-10 w-9 h-9 bg-white/90 hover:bg-white flex items-center justify-center shadow-lg rounded-full"
            data-testid="explorations-scroll-right"
          >
            <ChevronRight size={18} className="text-black" />
          </button>
        </div>

        {/* Desktop: Grid Layout - 2x2 */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-8 px-6 md:px-12 bg-white">
          {CATEGORIES.map((category) => (
            <ExplorationCard key={category.id} category={category} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Explorations;
